import { jsx, jsxs } from "react/jsx-runtime";
import { Box, useResponsiveValue, Loader, Checkbox, Textarea, Stack, Columns, Column, Text, Inline, TextLinkButton, Tag } from "braid-design-system";
import { JobAccessFormHeader } from "./styles/private/components/JobAccessFormHeader/index.mjs";
import { TeamMemberJobAccess } from "./styles/private/components/TeamMemberJobAccess/index.mjs";
import { createContext, useContext, useState, cloneElement } from "react";
import { EmailPreview } from "./styles/private/components/EmailPreview/index.mjs";
import { useTranslations, useLanguage } from "@vocab/react";
import { translations } from "./private/components/IncludeSenderOption/.vocab/index.mjs";
import { translations as translations$1 } from "./private/components/JobAccessEmailMessage/.vocab/index.mjs";
import { translations as translations$2 } from "./private/components/SendEmailReceiverList/.vocab/index.mjs";
import { Step } from "./styles/components/Step/index.mjs";
import gql from "graphql-tag";
import { translations as translations$3 } from "./provider/.vocab/index.mjs";
const JobAccessList = ({
  id,
  teamMembers,
  onSelectTeamMemberJobAccess,
  onSelectTeamMemberNotify
}) => /* @__PURE__ */ jsx(Box, { children: teamMembers.map((teamMember, index) => /* @__PURE__ */ jsx(
  TeamMemberJobAccess,
  {
    id,
    isOddRow: index % 2 === 0,
    teamMember,
    onSelectTeamMemberJobAccess,
    onSelectTeamMemberNotify
  },
  `row-${teamMember.userId}`
)) });
const isChangeable = (teamMember) => !teamMember.isJobCreator && !teamMember.isPending;
const isAccessChangeable = (teamMember) => isChangeable(teamMember) && !teamMember.hasAccessToAllJobs;
const teamMemberWithJobAccessSetting = (teamMember, isChecked) => {
  if (!isAccessChangeable(teamMember)) {
    return teamMember;
  }
  const updateNotifySelected = isChecked ? teamMember.notifySelected : false;
  return {
    ...teamMember,
    accessSelected: isChecked,
    notifySelected: updateNotifySelected
  };
};
const teamMemberWithNotifySetting = (teamMember, isChecked) => {
  if (!isChangeable(teamMember)) {
    return teamMember;
  }
  const updatedAccessSelected = isChecked && isAccessChangeable(teamMember) ? true : teamMember.accessSelected;
  return {
    ...teamMember,
    notifySelected: isChecked,
    accessSelected: updatedAccessSelected
  };
};
const teamMembersWithAccessChange = (teamMembers = [], isChecked, userId) => teamMembers.map((teamMember) => {
  if (userId && teamMember.userId !== userId) {
    return teamMember;
  }
  return teamMemberWithJobAccessSetting(teamMember, isChecked);
});
const teamMembersWithNotifyChange = (teamMembers = [], isChecked, userId) => teamMembers.map((teamMember) => {
  if (userId && teamMember.userId !== userId) {
    return teamMember;
  }
  return teamMemberWithNotifySetting(teamMember, isChecked);
});
const isTeamMemberAccessChanged = (teamMember) => {
  const { hasAccessToCurrentJob, accessSelected } = teamMember;
  return isAccessChangeable(teamMember) && hasAccessToCurrentJob !== accessSelected;
};
const isAllTeamMembersAccessCheckValue = (teamMembers) => teamMembers.filter((teamMember) => isAccessChangeable(teamMember)).map((teamMember) => teamMember.accessSelected);
const isAllTeamMembersNotifyCheckValue = (teamMembers) => teamMembers.filter((teamMember) => isChangeable(teamMember)).map((teamMember) => teamMember.notifySelected);
const hasTeamMemberNotifySelected = (updatedTeamMembers) => updatedTeamMembers.some((teamMember) => teamMember.notifySelected);
const getOnNotifyChangeEventData = (updatedTeamMembers) => {
  const teamMemberNotifySelected = hasTeamMemberNotifySelected(updatedTeamMembers);
  return {
    teamMemberNotifySelected
  };
};
const HirerTeamMemberJobAccessFormPresentation = ({
  id,
  loading = false,
  teamMembers = [],
  setTeamMembers,
  onNotifyChange
}) => {
  const onSelectTeamMemberJobAccess = (userId, isChecked) => {
    const updatedTeamMembers = teamMembersWithAccessChange(
      teamMembers,
      isChecked,
      userId
    );
    setTeamMembers(updatedTeamMembers);
  };
  const onSelectTeamMemberNotify = (userId, isChecked) => {
    const updatedTeamMembers = teamMembersWithNotifyChange(
      teamMembers,
      isChecked,
      userId
    );
    setTeamMembers(updatedTeamMembers);
    if (onNotifyChange) {
      const onNotifyEventData = getOnNotifyChangeEventData(updatedTeamMembers);
      onNotifyChange(onNotifyEventData);
    }
  };
  const onSelectAllJobAccess = (isChecked) => {
    const updatedTeamMembers = teamMembersWithAccessChange(
      teamMembers,
      isChecked
    );
    setTeamMembers(updatedTeamMembers);
  };
  const onSelectAllNotify = (isChecked) => {
    const updatedTeamMembers = teamMembersWithNotifyChange(
      teamMembers,
      isChecked
    );
    setTeamMembers(updatedTeamMembers);
    if (onNotifyChange) {
      const onNotifyEventData = getOnNotifyChangeEventData(updatedTeamMembers);
      onNotifyChange(onNotifyEventData);
    }
  };
  const listProps = {
    teamMembers,
    onSelectTeamMemberJobAccess,
    onSelectTeamMemberNotify
  };
  const isSelectAllJobAccessChecked = isAllTeamMembersAccessCheckValue(teamMembers);
  const isSelectAllNotifyChecked = isAllTeamMembersNotifyCheckValue(teamMembers);
  const headerProps = {
    isSelectAllJobAccessChecked,
    isSelectAllNotifyChecked,
    onSelectAllJobAccess,
    onSelectAllNotify
  };
  const hideHeader = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  return /* @__PURE__ */ jsxs(Box, { children: [
    hideHeader ? null : /* @__PURE__ */ jsx(JobAccessFormHeader, { ...headerProps, id }),
    loading ? /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", paddingLeft: "xlarge", children: /* @__PURE__ */ jsx(Loader, {}) }) : /* @__PURE__ */ jsx(JobAccessList, { ...listProps, id })
  ] });
};
const defaultJobAccessEmail = { emailMessage: "", includeSender: false };
const HirerTeamMemberJobAccessContext = createContext({
  jobId: "",
  seekAdvertiserId: "",
  currentLoginUserId: "",
  delayUpdate: false,
  setTeamMembers: (_) => {
  },
  isLoadingTeamMembers: false,
  setIsLoadingTeamMembers: (_) => {
  },
  teamMembers: [],
  jobAccessEmail: defaultJobAccessEmail,
  setJobAccessEmail: (_) => {
  }
});
const useHirerTeamMemberJobAccessContext = () => {
  const context = useContext(
    HirerTeamMemberJobAccessContext
  );
  if (context === void 0) {
    throw new Error(
      "context must be used within a HirerTeamMemberJobAccessProvider"
    );
  }
  return context;
};
const HirerTeamMemberJobAccessForm = ({ id, onNotifyChange }) => {
  const { teamMembers, setTeamMembers, isLoadingTeamMembers } = useHirerTeamMemberJobAccessContext();
  return /* @__PURE__ */ jsx(
    HirerTeamMemberJobAccessFormPresentation,
    {
      id,
      loading: isLoadingTeamMembers,
      teamMembers,
      setTeamMembers,
      onNotifyChange
    }
  );
};
const IncludeSenderOption = ({
  id,
  includeSender,
  setIncludeSender,
  hasMultipleReceivers = false
}) => {
  const { t } = useTranslations(translations);
  const onChange = (event) => {
    setIncludeSender(event.currentTarget.checked);
  };
  return /* @__PURE__ */ jsx(
    Checkbox,
    {
      id: `${id}-include-sender`,
      tone: "neutral",
      label: t("Include sender option label", {
        hasMultipleReceivers: hasMultipleReceivers ? "true" : "other"
      }),
      checked: includeSender,
      onChange
    }
  );
};
const MAX_EMAIL_CHARACTER_NUMBER = 5e3;
const MESSAGE_LINE_NUMBER = 7;
const JobAccessEmailMessage = ({ id, setMessage, message }) => {
  const { t } = useTranslations(translations$1);
  const onChange = (event) => {
    const value = event.currentTarget.value;
    setMessage(value);
  };
  const isOverCharacterLimit = message.length > MAX_EMAIL_CHARACTER_NUMBER;
  const maxEmailCharacterNumber = MAX_EMAIL_CHARACTER_NUMBER.toString();
  return /* @__PURE__ */ jsx(
    Textarea,
    {
      id: `${id}-email-content`,
      label: t("Add custom message"),
      secondaryLabel: t("Max characters", { maxEmailCharacterNumber }),
      autoFocus: true,
      value: message,
      characterLimit: MAX_EMAIL_CHARACTER_NUMBER,
      onChange,
      message: isOverCharacterLimit ? t("Max characters or less", { maxEmailCharacterNumber }) : void 0,
      tone: isOverCharacterLimit ? "critical" : "neutral",
      highlightRanges: isOverCharacterLimit ? [{ start: MAX_EMAIL_CHARACTER_NUMBER }] : [],
      grow: false,
      lines: MESSAGE_LINE_NUMBER
    }
  );
};
const JobAccessEmailContainer = ({
  id,
  setJobAccessEmail,
  jobAccessEmail,
  hasMultipleReceivers
}) => {
  const { emailMessage, includeSender } = jobAccessEmail;
  const setMessage = (message) => {
    setJobAccessEmail({ ...jobAccessEmail, emailMessage: message });
  };
  const setIncludeSender = (included) => {
    setJobAccessEmail({ ...jobAccessEmail, includeSender: included });
  };
  return /* @__PURE__ */ jsx(Box, { paddingTop: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(
      JobAccessEmailMessage,
      {
        id,
        message: emailMessage,
        setMessage
      }
    ),
    /* @__PURE__ */ jsx(
      IncludeSenderOption,
      {
        id,
        setIncludeSender,
        includeSender,
        hasMultipleReceivers
      }
    )
  ] }) });
};
const MAX_MEMBER_TAGS = 7;
const MemberTag = ({ firstName, lastName }) => {
  const { t } = useTranslations(translations$2);
  return /* @__PURE__ */ jsx(Box, { "data-hj-suppress": true, children: /* @__PURE__ */ jsx(Tag, { children: t("Member Tag", { firstName, lastName }) }) });
};
const SendEmailReceiverList = ({
  id,
  notifyMembers
}) => {
  const { t } = useTranslations(translations$2);
  const [showMore, setShowMore] = useState(false);
  const membersToShow = showMore ? notifyMembers : notifyMembers.slice(0, MAX_MEMBER_TAGS);
  const numberOfHiddenMembers = notifyMembers.slice(MAX_MEMBER_TAGS).length;
  return /* @__PURE__ */ jsx(
    Box,
    {
      id: `${id}-send-email-receiver-list`,
      data: { testid: `${id}-send-email-receiver-list` },
      children: /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
        /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { paddingTop: "xxsmall", children: /* @__PURE__ */ jsx(Text, { weight: "strong", baseline: false, children: t("Emailing:") }) }) }),
        /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Inline, { alignY: "center", space: "small", children: [
          membersToShow.map((member) => /* @__PURE__ */ jsx(
            MemberTag,
            {
              firstName: member.firstName,
              lastName: member.lastName
            },
            member.userId
          )),
          numberOfHiddenMembers > 0 && /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(TextLinkButton, { onClick: () => setShowMore(!showMore), children: t("Show more", {
            showMore: showMore ? "true" : "other",
            numberOfHiddenMembers: numberOfHiddenMembers.toString()
          }) }) })
        ] }) })
      ] })
    }
  );
};
const HirerTeamMemberSendEmailFormPresentation = ({
  id,
  notifyMembers = [],
  setJobAccessEmail,
  jobAccessEmail,
  senderFirstName,
  senderLastName,
  jobTitle = "",
  jobId = ""
}) => /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsx(SendEmailReceiverList, { id, notifyMembers }),
  /* @__PURE__ */ jsx(
    EmailPreview,
    {
      senderFirstName,
      senderLastName,
      jobTitle,
      jobId
    }
  ),
  /* @__PURE__ */ jsx(
    JobAccessEmailContainer,
    {
      id,
      setJobAccessEmail,
      jobAccessEmail,
      hasMultipleReceivers: notifyMembers.length > 1
    }
  )
] });
const HirerTeamMemberSendEmailForm = ({
  id,
  senderFirstName,
  senderLastName,
  jobTitle,
  jobId
}) => {
  const { teamMembers, jobAccessEmail, setJobAccessEmail } = useHirerTeamMemberJobAccessContext();
  const notifyMembers = teamMembers.filter(
    (teamMember) => teamMember.notifySelected
  );
  return /* @__PURE__ */ jsx(
    HirerTeamMemberSendEmailFormPresentation,
    {
      id,
      notifyMembers,
      jobAccessEmail,
      setJobAccessEmail,
      senderFirstName,
      senderLastName,
      jobTitle,
      jobId
    }
  );
};
const Stepper = ({
  title,
  stepNames,
  initialActiveStepIndex = 0,
  onActiveStepChanged = (_) => {
  },
  onClickHeaderPoint = (_fromStepIndex, _toStepIndex) => {
  },
  onComplete,
  onServerError,
  stepHeaderClassName,
  stepBodyClassName,
  stepContentClassName,
  children
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(
    initialActiveStepIndex
  );
  const [maxStepIndexVisited, setMaxStepIndexVisited] = useState(
    initialActiveStepIndex
  );
  const totalSteps = stepNames.length;
  const changeActiveStep = (index) => {
    setActiveStepIndex(index);
    onActiveStepChanged(index);
  };
  const goToStepIndex = (index) => {
    if (index < totalSteps && index >= 0 && index <= maxStepIndexVisited + 1) {
      changeActiveStep(index);
      if (maxStepIndexVisited < index) {
        setMaxStepIndexVisited(index);
      }
    } else if (index === totalSteps && onComplete) {
      onComplete();
      changeActiveStep(0);
    }
  };
  const steps = children.map(
    (child, index) => cloneElement(child, {
      ...child.props,
      key: `step-${index}`,
      title,
      stepNames,
      stepIndex: index,
      activeStepIndex,
      goToStepIndex,
      stepHeaderClassName,
      stepBodyClassName,
      stepContentClassName,
      onServerError,
      maxStepIndexVisited,
      onClickHeaderPoint
    })
  );
  return /* @__PURE__ */ jsx(Box, { height: "full", children: steps });
};
const HirerTeamMemberJobAccessProvider = ({
  jobId,
  seekAdvertiserId,
  currentLoginUserId,
  initialTeamMemberJobAccessList,
  delayUpdate = false,
  apolloClient,
  children
}) => {
  const [teamMembers, setTeamMembers] = useState(
    initialTeamMemberJobAccessList ?? []
  );
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [jobAccessEmail, setJobAccessEmail] = useState(
    defaultJobAccessEmail
  );
  const value = {
    jobId,
    seekAdvertiserId,
    currentLoginUserId,
    delayUpdate,
    teamMembers,
    setTeamMembers,
    isLoadingTeamMembers,
    setIsLoadingTeamMembers,
    jobAccessEmail,
    setJobAccessEmail,
    apolloClient
  };
  return /* @__PURE__ */ jsx(HirerTeamMemberJobAccessContext.Provider, { value, children });
};
const successResult = {
  success: true
};
const createServerErrorResult = (errorMessage) => ({
  success: false,
  error: { errorMessage, isServerError: true }
});
const createErrorResult = (errorMessage) => ({
  success: false,
  error: { errorMessage }
});
const GET_USERS_WITH_JOB_PERMISSION = gql`
  query HirerAccountUsersWithJobPermissions(
    $input: UsersWithJobPermissionInput!
  ) {
    hirerAccountUsersWithJobPermissions(input: $input) {
      userId
      firstName
      lastName
      email
      hasAccessToAllJobs
      hasAccessToCurrentJob
      isPending
      isJobCreator
      isAdmin
    }
  }
`;
const SET_USER_JOB_ACCESS = gql`
  mutation SetHirerAccountUsersJobAccess(
    $input: SetHirerAccountUsersJobAccessInput!
  ) {
    setHirerAccountUsersJobAccess(input: $input) {
      success
    }
  }
`;
const SEND_USER_JOB_ACCESS_EMAIL = gql`
  mutation SendHirerAccountUsersJobAccessEmail(
    $input: SendHirerAccountUsersJobAccessEmailInput!
  ) {
    sendHirerAccountUsersJobAccessEmail(input: $input) {
      success
    }
  }
`;
const sendUserJobAccessEmailVariables = (jobId, seekAdvertiserId, teamMembers, jobAccessEmail) => {
  const receivers = teamMembers.filter((member) => member.notifySelected).map(({ email, firstName, userId }) => ({
    email,
    firstName,
    auditUserId: userId
  }));
  if (receivers.length === 0) {
    return null;
  }
  const { emailMessage, includeSender } = jobAccessEmail;
  const trimmedEmail = (emailMessage ?? "").trim();
  return {
    input: {
      jobId,
      advertiserId: seekAdvertiserId,
      receivers,
      emailMessage: trimmedEmail.length === 0 ? void 0 : trimmedEmail,
      includeSender
    }
  };
};
const setUserJobAccessVariables = (jobId, seekAdvertiserId, teamMembers, delayUpdate) => {
  const teamMemberAccessList = teamMembers.filter((teamMember) => isTeamMemberAccessChanged(teamMember)).map(({ userId, accessSelected }) => ({
    userId,
    jobAccess: accessSelected
  }));
  if (teamMemberAccessList.length === 0) {
    return null;
  }
  return {
    input: {
      jobId,
      advertiserId: seekAdvertiserId,
      accessList: teamMemberAccessList,
      delayUpdate
    }
  };
};
const getUsersWithJobPermissionVariables = (jobId, seekAdvertiserId) => ({
  input: {
    advertiserId: seekAdvertiserId,
    jobId
  }
});
const saveTeamMemberJobAccess = async (jobId, seekAdvertiserId, teamMembers, delayUpdate, client) => {
  var _a;
  const setAccessVariables = setUserJobAccessVariables(
    jobId,
    seekAdvertiserId,
    teamMembers,
    delayUpdate
  );
  if (setAccessVariables === null) {
    return true;
  }
  const { data } = await client.mutate({
    mutation: SET_USER_JOB_ACCESS,
    variables: setAccessVariables
  });
  return ((_a = data == null ? void 0 : data.setHirerAccountUsersJobAccess) == null ? void 0 : _a.success) ?? false;
};
const sendTeamMemberJobAccessEmail = async (jobId, seekAdvertiserId, teamMembers, jobAccessEmail, client) => {
  var _a;
  const sendEmailVariables = sendUserJobAccessEmailVariables(
    jobId,
    seekAdvertiserId,
    teamMembers,
    jobAccessEmail
  );
  if (sendEmailVariables === null) {
    return true;
  }
  const { data } = await client.mutate({
    mutation: SEND_USER_JOB_ACCESS_EMAIL,
    variables: sendEmailVariables
  });
  return ((_a = data == null ? void 0 : data.sendHirerAccountUsersJobAccessEmail) == null ? void 0 : _a.success) ?? false;
};
const saveTeamMemberAccessAndNotify = async (jobId, seekAdvertiserId, teamMembers, jobAccessEmail, delayUpdate, client, messages) => {
  try {
    const { emailMessage } = jobAccessEmail;
    if (emailMessage.length > MAX_EMAIL_CHARACTER_NUMBER) {
      return createErrorResult("");
    }
    const saveJobAccessResult = await saveTeamMemberJobAccess(
      jobId,
      seekAdvertiserId,
      teamMembers,
      delayUpdate,
      client
    );
    if (!saveJobAccessResult) {
      throw new Error(messages["set job access failed"]);
    }
    const sendJobAccessEmailResult = await sendTeamMemberJobAccessEmail(
      jobId,
      seekAdvertiserId,
      teamMembers,
      jobAccessEmail,
      client
    );
    if (!sendJobAccessEmailResult) {
      throw new Error(messages["send job access email failed"]);
    }
    return successResult;
  } catch {
    return createServerErrorResult(
      messages["Sorry we are having trouble setting your team access now."]
    );
  }
};
const getSaveEventData = (teamMembers, jobAccessEmail) => {
  const notificationTeamAddCount = teamMembers.filter(
    (member) => member.notifySelected
  ).length;
  let notificationJobAccessAddCount = 0;
  let notificationJobAccessRemoveCount = 0;
  teamMembers.forEach((member) => {
    if (isTeamMemberAccessChanged(member)) {
      if (member.accessSelected) {
        notificationJobAccessAddCount++;
      } else {
        notificationJobAccessRemoveCount++;
      }
    }
  });
  const saveEventData = {
    notificationTeamMemberCount: teamMembers.length,
    notificationJobAccessAddCount,
    notificationJobAccessRemoveCount,
    notificationTeamAddCount,
    notificationScope: "team"
  };
  if (notificationTeamAddCount > 0) {
    return {
      ...saveEventData,
      notificationEmailIncludeSender: jobAccessEmail.includeSender,
      notificationEmailCustomMessage: jobAccessEmail.emailMessage.length > 0
    };
  }
  return saveEventData;
};
const translationMessages = (t) => ({
  "Sorry we are having trouble setting your team access now.": t(
    "Sorry we are having trouble setting your team access now."
  ),
  "set job access failed": t("set job access failed"),
  "send job access email failed": t("send job access email failed")
});
const useSaveHirerTeamMemberJobAccessAndNotify = () => {
  const { t } = useTranslations(translations$3);
  const messages = translationMessages(t);
  const {
    jobId,
    seekAdvertiserId,
    delayUpdate,
    teamMembers = [],
    apolloClient,
    jobAccessEmail
  } = useHirerTeamMemberJobAccessContext();
  return async (onSaveTracking) => {
    if (onSaveTracking) {
      onSaveTracking(getSaveEventData(teamMembers, jobAccessEmail));
    }
    return saveTeamMemberAccessAndNotify(
      jobId,
      seekAdvertiserId,
      teamMembers,
      jobAccessEmail,
      delayUpdate,
      apolloClient,
      messages
    );
  };
};
const convertToTeamMemberForAccess = (accountUser) => {
  const { hasAccessToAllJobs, hasAccessToCurrentJob } = accountUser;
  return {
    ...accountUser,
    accessSelected: hasAccessToAllJobs || hasAccessToCurrentJob,
    notifySelected: false
  };
};
const sortByAllJobAccessAndNameDescending = (a, b, formatterFunction, language) => {
  const jobCreatorCompare = Number(b.isJobCreator) - Number(a.isJobCreator);
  const accessCompare = Number(b.hasAccessToAllJobs || b.hasAccessToCurrentJob) - Number(a.hasAccessToAllJobs || a.hasAccessToCurrentJob);
  const nameCompare = formatterFunction({
    firstName: a.firstName,
    lastName: a.lastName
  }).toLocaleLowerCase(language).localeCompare(
    formatterFunction({
      firstName: b.firstName,
      lastName: b.lastName
    }).toLocaleLowerCase(language)
  );
  return jobCreatorCompare || accessCompare || nameCompare;
};
const getSortedTeamMembers = (accountUsers, currentUserId, formatterFunction, language) => {
  const teamMembers = (accountUsers ?? []).filter((user) => user.userId !== currentUserId).map((user) => convertToTeamMemberForAccess(user));
  return teamMembers.sort(
    (a, b) => sortByAllJobAccessAndNameDescending(a, b, formatterFunction, language)
  );
};
const getTeamMembers = async (jobId, seekAdvertiserId, userId, apolloClient, formatterFunction, language) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_USERS_WITH_JOB_PERMISSION,
      variables: getUsersWithJobPermissionVariables(jobId, seekAdvertiserId),
      fetchPolicy: "no-cache"
    });
    const accountUsers = data.hirerAccountUsersWithJobPermissions ?? [];
    return {
      teamMemberJobAccessList: getSortedTeamMembers(
        accountUsers,
        userId,
        formatterFunction,
        language
      )
    };
  } catch {
    return { teamMemberJobAccessList: [], error: true };
  }
};
const useGetHirerTeamMemberJobAccessAccessList = () => {
  const { t } = useTranslations(translations$3);
  const formatterFunction = ({ firstName, lastName }) => t("Full name format", { firstName, lastName });
  const { language } = useLanguage();
  const {
    jobId,
    seekAdvertiserId,
    currentLoginUserId,
    setTeamMembers,
    isLoadingTeamMembers,
    setIsLoadingTeamMembers,
    apolloClient
  } = useHirerTeamMemberJobAccessContext();
  return async (onLoadCompleted = (_) => {
  }) => {
    if (isLoadingTeamMembers) {
      return;
    }
    setIsLoadingTeamMembers(true);
    const result = await getTeamMembers(
      jobId,
      seekAdvertiserId,
      currentLoginUserId,
      apolloClient,
      formatterFunction,
      language
    );
    setTeamMembers(result.teamMemberJobAccessList);
    onLoadCompleted(result);
    setIsLoadingTeamMembers(false);
  };
};
export {
  GET_USERS_WITH_JOB_PERMISSION,
  HirerTeamMemberJobAccessForm,
  HirerTeamMemberJobAccessFormPresentation,
  HirerTeamMemberJobAccessProvider,
  HirerTeamMemberSendEmailForm,
  HirerTeamMemberSendEmailFormPresentation,
  Step,
  Stepper,
  useGetHirerTeamMemberJobAccessAccessList,
  useSaveHirerTeamMemberJobAccessAndNotify
};
